// Lightweight API client for ASSK backend
import { toast } from "sonner";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "assk_token";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(t) {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}

function authHeaders(extra = {}) {
  const t = getToken();
  return { ...(t ? { Authorization: `Bearer ${t}` } : {}), ...extra };
}

// Fetch an uploaded file (with auth) and return a same-origin blob object URL
// plus its content type, so it can be previewed inline (iframe/img) instead of
// being downloaded.
export async function fetchFileObjectUrl(fileId) {
  const res = await fetch(`${BACKEND_URL}/api/files/${fileId}`, { headers: authHeaders() });
  const ct = (res.headers.get("content-type") || "").toLowerCase();
  if (!res.ok || ct.includes("text/html")) throw new Error("file-open-failed");
  const raw = await res.blob();
  // Force a preview-friendly MIME type (some blobs come back as octet-stream,
  // which makes browsers download instead of preview).
  const type = raw.type && raw.type !== "application/octet-stream" ? raw.type : (ct || "application/pdf");
  const blob = raw.type === type ? raw : new Blob([raw], { type });
  return { url: URL.createObjectURL(blob), contentType: type };
}

// Open an uploaded document as an IN-APP preview (modal iframe) instead of a new
// browser tab. This guarantees applicant documents are shown, never downloaded,
// across every officer/admin/applicant view. Rendered by <FilePreviewHost/>.
export function openFileById(fileId, filename) {
  window.dispatchEvent(new CustomEvent("assk:preview-file", { detail: { fileId, filename } }));
  return true;
}

async function handle(res) {
  if (!res.ok) {
    let detail = "काहीतरी चूक झाली.";
    try {
      const j = await res.json();
      detail = j.detail || detail;
    } catch (e) {}
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export const api = {
  register: (body) =>
    fetch(`${API}/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then(handle),

  registerRequestOtp: (body) =>
    fetch(`${API}/auth/register/request-otp`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then(handle),

  registerVerify: (body) =>
    fetch(`${API}/auth/register/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then(handle),

  registerComplete: (body) =>
    fetch(`${API}/auth/register/complete`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then(handle),

  login: (body) =>
    fetch(`${API}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then(handle),

  resetRequestOtp: (body) =>
    fetch(`${API}/auth/reset/request-otp`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then(handle),
  resetVerify: (body) =>
    fetch(`${API}/auth/reset/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then(handle),
  resetComplete: (body) =>
    fetch(`${API}/auth/reset/complete`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then(handle),

  me: () => fetch(`${API}/auth/me`, { headers: authHeaders() }).then(handle),

  getMyApplication: () =>
    fetch(`${API}/application/me`, { headers: authHeaders() }).then(handle),

  newApplication: () =>
    fetch(`${API}/application/new`, { method: "POST", headers: authHeaders() }).then(handle),

  updateApplication: (id, body) =>
    fetch(`${API}/application/${id}`, {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    }).then(handle),

  requestSubmitOtp: (id) =>
    fetch(`${API}/application/${id}/request-submit-otp`, { method: "POST", headers: authHeaders() }).then(handle),

  submitApplication: (id, code) =>
    fetch(`${API}/application/${id}/submit`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ code }),
    }).then(handle),

  uploadFile: (id, field, file) => {
    const fd = new FormData();
    fd.append("field", field);
    fd.append("file", file);
    return fetch(`${API}/application/${id}/files`, {
      method: "POST",
      headers: authHeaders(),
      body: fd,
    }).then(handle);
  },

  fileUrl: (fileId) => `${API}/files/${fileId}`,

  circlesFor: (tehsil) =>
    fetch(`${API}/circles?tehsil=${encodeURIComponent(tehsil)}`).then(handle),

  localBodiesFor: (tehsil, circle, orgType) =>
    fetch(`${API}/local-bodies?tehsil=${encodeURIComponent(tehsil)}&circle=${encodeURIComponent(circle)}&orgType=${encodeURIComponent(orgType)}`).then(handle),

  localBodiesByType: (orgType) =>
    fetch(`${API}/local-bodies-by-type?orgType=${encodeURIComponent(orgType)}`).then(handle),

  wardsFor: (localBody) =>
    fetch(`${API}/wards?localBody=${encodeURIComponent(localBody)}`).then(handle),

  circleApplications: () =>
    fetch(`${API}/officer/circle/applications`, { headers: authHeaders() }).then(handle),

  circleVerify: (id, body) =>
    fetch(`${API}/officer/applications/${id}/circle-verify`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    }).then(handle),

  circleUploadPhoto: (id, field, file) => {
    const fd = new FormData();
    fd.append("field", field);
    fd.append("file", file);
    return fetch(`${API}/officer/applications/${id}/verify-photo`, {
      method: "POST",
      headers: authHeaders(),
      body: fd,
    }).then(handle);
  },

  rdcSendPhysical: (tehsil) =>
    fetch(`${API}/officer/rdc/send-physical`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ tehsil }),
    }).then(handle),

  rdcPhysicalList: () =>
    fetch(`${API}/officer/rdc/physical-list`, { headers: authHeaders() }).then(handle),
  rdcPullbackPhysical: (tehsil) =>
    fetch(`${API}/officer/rdc/pullback-physical`, {
      method: "POST", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify({ tehsil }),
    }).then(handle),
  rdcPrelimPending: (tehsil) =>
    fetch(`${API}/officer/rdc/prelim-pending?tehsil=${encodeURIComponent(tehsil)}`, { headers: authHeaders() }).then(handle),
  rdcSendBackPrelim: (id, reason) =>
    fetch(`${API}/officer/rdc/applications/${id}/send-back-prelim`, {
      method: "POST", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify({ reason }),
    }).then(handle),

  rdcMarkingList: () =>
    fetch(`${API}/officer/rdc/marking-list`, { headers: authHeaders() }).then(handle),
  rdcMarking: (id, body) =>
    fetch(`${API}/officer/applications/${id}/marking`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    }).then(handle),

  rdcInterviewList: () =>
    fetch(`${API}/officer/rdc/interview-list`, { headers: authHeaders() }).then(handle),
  rdcInterviewSlot: (body) =>
    fetch(`${API}/officer/rdc/interview-slot`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    }).then(handle),
  rdcInterviewSend: (body) =>
    fetch(`${API}/officer/rdc/interview-send`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    }).then(handle),
  rdcInterviewPdf: async (tehsil, date, time) => {
    const qs = `tehsil=${encodeURIComponent(tehsil)}&date=${encodeURIComponent(date || "")}&time=${encodeURIComponent(time || "")}`;
    const res = await fetch(`${API}/officer/rdc/interview-pdf?${qs}`, { headers: authHeaders() });
    if (!res.ok) {
      let m = "PDF डाउनलोड अयशस्वी.";
      try { m = (await res.json()).detail || m; } catch (e) { /* noop */ }
      throw new Error(m);
    }
    const blob = await res.blob();
    const name = `मुलाखत-पत्रे-${tehsil}.pdf`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    return { name, size: blob.size };
  },

  aroInterviewList: () =>
    fetch(`${API}/officer/aro/interview-list`, { headers: authHeaders() }).then(handle),
  aroInterviewMarking: (id, body) =>
    fetch(`${API}/officer/applications/${id}/interview-marking`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    }).then(handle),

  markingList: () =>
    fetch(`${API}/officer/marking-list`, { headers: authHeaders() }).then(handle),
  sendMarking: (tehsil) =>
    fetch(`${API}/officer/rdc/send-marking`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ tehsil }),
    }).then(handle),
  markingRelease: () =>
    fetch(`${API}/officer/rdc/marking-release`, { headers: authHeaders() }).then(handle),
  rdcDispatchStatus: () =>
    fetch(`${API}/officer/rdc/dispatch-status`, { headers: authHeaders() }).then(handle),
  sectionMarking: (id, body) =>
    fetch(`${API}/officer/applications/${id}/section-marking`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    }).then(handle),

  resultList: () =>
    fetch(`${API}/officer/result-list`, { headers: authHeaders() }).then(handle),

  resultApprove: (appId) =>
    fetch(`${API}/officer/result/approve`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ appId }),
    }).then(handle),

  sendApprovalLetter: (appId) =>
    fetch(`${API}/officer/result/send-approval-letter/${appId}`, {
      method: "POST",
      headers: authHeaders(),
    }).then(handle),

  sendAllApprovalLetters: () =>
    fetch(`${API}/officer/result/send-all-approval-letters`, {
      method: "POST",
      headers: authHeaders(),
    }).then(handle),

  auditExport: (body) =>
    fetch(`${API}/audit/export`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    }).then(handle).catch(() => {}),

  adminAuditLogs: (action = "") =>
    fetch(`${API}/admin/audit-logs?action=${encodeURIComponent(action)}`, { headers: authHeaders() }).then(handle),

  getDeadline: () =>
    fetch(`${API}/settings/deadline`).then(handle),
  setDeadline: (body) =>
    fetch(`${API}/admin/settings/deadline`, {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    }).then(handle),

  tehsildarConfirmList: () =>
    fetch(`${API}/officer/tehsildar/confirm-list`, { headers: authHeaders() }).then(handle),

  tehsildarConfirm: (id, body) =>
    fetch(`${API}/officer/applications/${id}/tehsildar-confirm`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    }).then(handle),

  adminList: (search = "", status = "") =>
    fetch(`${API}/admin/applications?search=${encodeURIComponent(search)}&status=${encodeURIComponent(status)}`, {
      headers: authHeaders(),
    }).then(handle),

  adminStats: () => fetch(`${API}/admin/stats`, { headers: authHeaders() }).then(handle),

  adminCircleOfficers: () => fetch(`${API}/admin/circle-officers`, { headers: authHeaders() }).then(handle),

  adminStorageStats: () => fetch(`${API}/admin/storage-stats`, { headers: authHeaders() }).then(handle),

  adminClearApplicationData: (confirm) =>
    fetch(`${API}/admin/clear-application-data`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ confirm }),
    }).then(handle),

  adminChangePassword: (currentPassword, newPassword) =>
    fetch(`${API}/admin/change-password`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ currentPassword, newPassword }),
    }).then(handle),

  accountantPayments: () => fetch(`${API}/accountant/payments`, { headers: authHeaders() }).then(handle),
  accountantPaymentStats: () => fetch(`${API}/accountant/payments/stats`, { headers: authHeaders() }).then(handle),

  accountantVerify: (id, body) =>
    fetch(`${API}/accountant/payments/${id}/verify`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    }).then(handle),

  rdcPayments: () => fetch(`${API}/rdc/payments`, { headers: authHeaders() }).then(handle),

  rdcReviewPayment: (id, note) =>
    fetch(`${API}/rdc/payments/${id}/review`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ note }),
    }).then(handle),


  adminBackup: async () => {
    const res = await fetch(`${API}/admin/backup`, { headers: authHeaders() });
    if (!res.ok) throw new Error("बॅकअप तयार करता आले नाही");
    const blob = await res.blob();
    const cd = res.headers.get("Content-Disposition") || "";
    const m = cd.match(/filename="?([^"]+)"?/);
    const name = m ? m[1] : `assk-backup-${Date.now()}.json`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = name; document.body.appendChild(a); a.click();
    a.remove(); URL.revokeObjectURL(url);
    return { name, size: blob.size };
  },

  adminDetail: (id) => fetch(`${API}/admin/applications/${id}`, { headers: authHeaders() }).then(handle),

  adminReview: (id, body) =>
    fetch(`${API}/admin/applications/${id}/review`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    }).then(handle),

  // ---- officer (RBAC) ----
  officerLogin: (body) =>
    fetch(`${API}/auth/officer/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then(handle),

  changePassword: (body) =>
    fetch(`${API}/auth/change-password`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    }).then(handle),

  officerMeta: () => fetch(`${API}/officer/meta`, { headers: authHeaders() }).then(handle),
  vacancySummary: () => fetch(`${API}/officer/vacancy-summary`, { headers: authHeaders() }).then(handle),

  officerList: (search = "", status = "", tehsil = "", includeDrafts = false) =>
    fetch(`${API}/officer/applications?search=${encodeURIComponent(search)}&status=${encodeURIComponent(status)}&tehsil=${encodeURIComponent(tehsil)}&include_drafts=${includeDrafts ? "true" : "false"}`, {
      headers: authHeaders(),
    }).then(handle),

  officerStats: () => fetch(`${API}/officer/stats`, { headers: authHeaders() }).then(handle),

  officerDetail: (id) => fetch(`${API}/officer/applications/${id}`, { headers: authHeaders() }).then(handle),

  officerAction: (id, body) =>
    fetch(`${API}/officer/applications/${id}/action`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    }).then(handle),

  officerPrelim: (id, body) =>
    fetch(`${API}/officer/applications/${id}/prelim`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    }).then(handle),

  officerPrelimStats: () => fetch(`${API}/officer/prelim-stats`, { headers: authHeaders() }).then(handle),

  officerTehsilStats: () => fetch(`${API}/officer/tehsil-stats`, { headers: authHeaders() }).then(handle),

  officerLocalBodyStats: (tehsil = "") => fetch(`${API}/officer/local-body-stats?tehsil=${encodeURIComponent(tehsil)}`, { headers: authHeaders() }).then(handle),

  sdoReview: (id, body) =>
    fetch(`${API}/officer/applications/${id}/sdo-review`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    }).then(handle),

  rdcReview: (id, body) =>
    fetch(`${API}/officer/applications/${id}/rdc-review`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    }).then(handle),

  officerAccounts: () => fetch(`${API}/officer/admin/accounts`, { headers: authHeaders() }).then(handle),

  resetAccountPw: (id, body) =>
    fetch(`${API}/officer/admin/accounts/${id}/reset-password`, {
      method: "POST", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify(body || {}),
    }).then(handle),

  toggleAccount: (id) =>
    fetch(`${API}/officer/admin/accounts/${id}/toggle-active`, { method: "POST", headers: authHeaders() }).then(handle),

  reassignAccount: (id, body) =>
    fetch(`${API}/officer/admin/accounts/${id}/reassign`, {
      method: "POST", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify(body),
    }).then(handle),
};
